import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Breadcrumbs, 
  Link, 
  Button, 
  Grid, 
  Card, 
  CardContent,
  Chip,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
  Link as MuiLink,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  CardActions
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningIcon from '@mui/icons-material/Warning';
import KeyIcon from '@mui/icons-material/Key';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import UpdateIcon from '@mui/icons-material/Update';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { License, LicenseDetail, licenseService } from '../../service/licenseService';

const LicenseAdmin: React.FC = () => {
  // State für die Liste der Lizenzen
  const [licenses, setLicenses] = useState<License[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<'cards' | 'table'>('cards');
  const navigate = useNavigate();

  // State für Lizenzdetails und Modals
  const [selectedLicense, setSelectedLicense] = useState<LicenseDetail | null>(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [extendLicenseModalOpen, setExtendLicenseModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [confirmRevokeModalOpen, setConfirmRevokeModalOpen] = useState(false);

  // Formular-State für Bearbeitung (vereinfacht)
  const [editForm, setEditForm] = useState({
    licenseStatus: '',
    subscribedUntil: ''
  });

  // Formular-State für Lizenzverlängerung
  const [extendForm, setExtendForm] = useState({
    durationMonths: 12,
    updateStatus: true
  });

  // Lizenzen beim ersten Render laden
  useEffect(() => {
    fetchLicenses();
  }, []);

  // Alle Lizenzen vom Server laden
  const fetchLicenses = async () => {
    try {
      setLoading(true);
      const loadedLicenses = await licenseService.getAllLicenses();
      setLicenses(loadedLicenses);
      setError(null);
    } catch (err) {
      console.error('Fehler beim Laden der Lizenzen:', err);
      setError('Die Lizenzen konnten nicht geladen werden. Bitte versuchen Sie es später erneut.');
    } finally {
      setLoading(false);
    }
  };

  // Farbzuordnung für Lizenztypen
  const getLicenseTypeColor = (type: string) => {
    return 'primary'; // Immer primary, da es nur einen Typ gibt
  };

  // Farbzuordnung für Lizenzstatus
  const getLicenseStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'expired': return 'error';
      case 'trial': return 'warning';
      case 'pending': return 'info';
      default: return 'default';
    }
  };

  // Details einer Lizenz anzeigen
  const handleViewDetails = async (id: string) => {
    try {
      setLoading(true);
      const licenseDetails = await licenseService.getLicenseDetails(id);
      setSelectedLicense(licenseDetails);
      setDetailModalOpen(true);
    } catch (err) {
      console.error('Fehler beim Laden der Lizenzdetails:', err);
      setError('Die Lizenzdetails konnten nicht geladen werden.');
    } finally {
      setLoading(false);
    }
  };

  // Lizenz-Bearbeitungsdialog öffnen
  const handleEditLicense = (license: License) => {
    setSelectedLicense(license as LicenseDetail);
    setEditForm({
      licenseStatus: license.licenseStatus,
      subscribedUntil: license.subscribedUntil || ''
    });
    setEditModalOpen(true);
  };

  // Lizenz aktualisieren
  const handleUpdateLicense = async () => {
    if (!selectedLicense) return;
    
    try {
      setLoading(true);
      
      await licenseService.updateLicense(selectedLicense.id, {
        licenseStatus: editForm.licenseStatus,
        subscribedUntil: editForm.subscribedUntil
      });
      
      setSuccess('Lizenz wurde erfolgreich aktualisiert');
      setEditModalOpen(false);
      fetchLicenses();
    } catch (err) {
      console.error('Fehler beim Aktualisieren der Lizenz:', err);
      setError('Die Lizenz konnte nicht aktualisiert werden.');
    } finally {
      setLoading(false);
    }
  };

  // Lizenz-Verlängerungsdialog öffnen
  const handleOpenExtendLicense = (license: License) => {
    setSelectedLicense(license as LicenseDetail);
    setExtendForm({
      durationMonths: 12,
      updateStatus: true
    });
    setExtendLicenseModalOpen(true);
  };

  // Lizenz verlängern
  const handleExtendLicense = async () => {
    if (!selectedLicense) return;
    
    try {
      setLoading(true);
      
      // Aktuelle Daten der Lizenz
      const currentEndDate = selectedLicense.subscribedUntil 
        ? new Date(selectedLicense.subscribedUntil) 
        : new Date();
      
      // Falls die Lizenz abgelaufen ist, beginnen wir ab heute
      const startDate = currentEndDate < new Date() ? new Date() : currentEndDate;
      
      // Berechne das neue Ablaufdatum
      const newEndDate = new Date(startDate);
      newEndDate.setMonth(newEndDate.getMonth() + extendForm.durationMonths);
      
      // Update der Lizenz
      const updateData: any = {
        subscribedUntil: newEndDate.toISOString().split('T')[0]
      };
      
      // Status auf "active" setzen, wenn gewünscht
      if (extendForm.updateStatus) {
        updateData.licenseStatus = 'active';
      }
      
      await licenseService.updateLicense(selectedLicense.id, updateData);
      
      setSuccess(`Lizenz wurde erfolgreich bis zum ${formatDate(updateData.subscribedUntil)} verlängert`);
      setExtendLicenseModalOpen(false);
      fetchLicenses();
    } catch (err) {
      console.error('Fehler beim Verlängern der Lizenz:', err);
      setError('Die Lizenz konnte nicht verlängert werden.');
    } finally {
      setLoading(false);
    }
  };

  // Datumsformatierung
  const formatDate = (dateString: string | null): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Breadcrumbs Navigation */}
      <Breadcrumbs aria-label="Navigationspfad" sx={{ mb: 2 }}>
        <MuiLink 
          color="inherit" 
          component="button"
          variant="body1"
          onClick={() => navigate('/dashboard')}
          sx={{ textDecoration: 'none' }}
        >
          Dashboard
        </MuiLink>
        <MuiLink 
          color="inherit" 
          component="button"
          variant="body1"
          onClick={() => navigate('/backoffice')}
          sx={{ textDecoration: 'none' }}
        >
          Administration
        </MuiLink>
        <Typography color="text.primary">Lizenzverwaltung</Typography>
      </Breadcrumbs>
      
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton 
            sx={{ mr: 2 }}
            onClick={() => navigate('/backoffice')}
            aria-label="Zurück zur Administration"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            Lizenzverwaltung
          </Typography>
        </Box>
        <Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLicenses}
          >
            Aktualisieren
          </Button>
        </Box>
      </Box>

      {/* Fehlermeldung */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Erfolgsmeldung */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={() => setSuccess(null)}
      >
        <Alert onClose={() => setSuccess(null)} severity="success">
          {success}
        </Alert>
      </Snackbar>

      {/* Ansichtswechsel */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs 
          value={viewMode} 
          onChange={(_, newValue) => setViewMode(newValue)}
          aria-label="Ansichtsmodus"
        >
          <Tab label="Kacheln" value="cards" />
          <Tab label="Tabelle" value="table" />
        </Tabs>
      </Box>

      {/* Ladeanzeige */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Kachelansicht */}
      {!loading && viewMode === 'cards' && (
        <Grid container spacing={3}>
          {licenses.map(license => (
            <Grid item xs={12} sm={6} md={4} lg={3} key={license.id} sx={{ mb: 3 }}>
              <Card 
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: 4
                  }
                }}
              >
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6" component="div">
                      {license.displayName}
                    </Typography>
                    <Chip 
                      label={license.licenseType} 
                      color={getLicenseTypeColor(license.licenseType) as any}
                      size="small"
                    />
                  </Box>
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {license.email}
                  </Typography>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Chip 
                      icon={license.isActive ? <CheckCircleOutlineIcon /> : <WarningIcon />}
                      label={license.licenseStatus} 
                      color={getLicenseStatusColor(license.licenseStatus) as any}
                      size="small"
                      sx={{ mr: 1 }}
                    />
                    <Typography variant="body2">
                      {license.daysRemaining > 0 ? `${license.daysRemaining} Tage verbleibend` : 'Abgelaufen'}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Box>
                    <Button 
                      size="small" 
                      onClick={() => handleViewDetails(license.id)}
                      variant="outlined"
                    >
                      Details
                    </Button>
                  </Box>
                  <Box>
                    <IconButton 
                      size="small"
                      onClick={() => handleOpenExtendLicense(license)}
                      title="Lizenz verlängern"
                      color="secondary"
                    >
                      <CalendarMonthIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => handleEditLicense(license)}
                      title="Lizenz bearbeiten"
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Box>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Tabellenansicht */}
      {!loading && viewMode === 'table' && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Kunde</TableCell>
                <TableCell>E-Mail</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Ablaufdatum</TableCell>
                <TableCell>Verfügbare Tage</TableCell>
                <TableCell>Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {licenses.map(license => (
                <TableRow key={license.id}>
                  <TableCell>{license.displayName}</TableCell>
                  <TableCell>{license.email}</TableCell>
                  <TableCell>
                    <Chip 
                      icon={license.isActive ? <CheckCircleOutlineIcon /> : <WarningIcon />}
                      label={license.licenseStatus} 
                      color={getLicenseStatusColor(license.licenseStatus) as any}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {formatDate(license.subscribedUntil)}
                  </TableCell>
                  <TableCell>
                    {license.daysRemaining > 0 ? license.daysRemaining : 'Abgelaufen'}
                  </TableCell>
                  <TableCell>
                    <Button 
                      size="small" 
                      onClick={() => handleViewDetails(license.id)}
                      variant="outlined"
                      sx={{ mr: 1 }}
                    >
                      Details
                    </Button>
                    <IconButton 
                      size="small"
                      onClick={() => handleOpenExtendLicense(license)}
                      title="Lizenz verlängern"
                      color="secondary"
                    >
                      <CalendarMonthIcon fontSize="small" />
                    </IconButton>
                    <IconButton 
                      size="small"
                      onClick={() => handleEditLicense(license)}
                      title="Lizenz bearbeiten"
                      color="primary"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Lizenzverlängerungsdialog */}
      <Dialog open={extendLicenseModalOpen} onClose={() => setExtendLicenseModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Lizenz verlängern
          <IconButton
            aria-label="close"
            onClick={() => setExtendLicenseModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedLicense && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedLicense.displayName} - {selectedLicense.email}
              </Typography>
              
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Aktueller Status:</Typography>
                    <Chip 
                      label={selectedLicense.licenseStatus} 
                      color={getLicenseStatusColor(selectedLicense.licenseStatus) as any}
                      size="small"
                    />
                  </Box>
                </Grid>
                
                <Grid item xs={12}>
                  <Typography variant="body2">
                    Aktuelles Ablaufdatum: {formatDate(selectedLicense.subscribedUntil)}
                  </Typography>
                </Grid>
              </Grid>
              
              <Box sx={{ mt: 3 }}>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel id="extend-duration-label">Verlängerungsdauer</InputLabel>
                  <Select
                    labelId="extend-duration-label"
                    value={extendForm.durationMonths}
                    label="Verlängerungsdauer"
                    onChange={(e) => setExtendForm({ ...extendForm, durationMonths: Number(e.target.value) })}
                  >
                    <MenuItem value={1}>1 Monat</MenuItem>
                    <MenuItem value={3}>3 Monate</MenuItem>
                    <MenuItem value={6}>6 Monate</MenuItem>
                    <MenuItem value={12}>1 Jahr</MenuItem>
                    <MenuItem value={24}>2 Jahre</MenuItem>
                    <MenuItem value={36}>3 Jahre</MenuItem>
                  </Select>
                </FormControl>
                
                <FormControlLabel
                  control={
                    <Checkbox 
                      checked={extendForm.updateStatus} 
                      onChange={(e) => setExtendForm({ ...extendForm, updateStatus: e.target.checked })}
                    />
                  }
                  label="Status auf 'Aktiv' setzen"
                />
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExtendLicenseModalOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleExtendLicense} 
            variant="contained" 
            color="primary"
            startIcon={<CalendarMonthIcon />}
          >
            Lizenz verlängern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bearbeitungsdialog */}
      <Dialog open={editModalOpen} onClose={() => setEditModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Lizenz bearbeiten
          <IconButton
            aria-label="close"
            onClick={() => setEditModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedLicense && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {selectedLicense.displayName} - {selectedLicense.email}
              </Typography>
              
              <FormControl fullWidth margin="normal">
                <InputLabel id="license-status-label">Lizenzstatus</InputLabel>
                <Select
                  labelId="license-status-label"
                  value={editForm.licenseStatus}
                  label="Lizenzstatus"
                  onChange={(e) => setEditForm({ ...editForm, licenseStatus: e.target.value })}
                >
                  <MenuItem value="active">Aktiv</MenuItem>
                  <MenuItem value="pending">Ausstehend</MenuItem>
                  <MenuItem value="trial">Trial</MenuItem>
                  <MenuItem value="expired">Abgelaufen</MenuItem>
                </Select>
              </FormControl>
              
              <TextField
                fullWidth
                margin="normal"
                label="Gültig bis"
                type="date"
                value={editForm.subscribedUntil ? editForm.subscribedUntil.substring(0, 10) : ''}
                onChange={(e) => setEditForm({ ...editForm, subscribedUntil: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditModalOpen(false)}>Abbrechen</Button>
          <Button 
            onClick={handleUpdateLicense} 
            variant="contained" 
            color="primary"
            startIcon={<EditIcon />}
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Details-Dialog */}
      <Dialog open={detailModalOpen} onClose={() => setDetailModalOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          Lizenzdetails
          <IconButton
            aria-label="close"
            onClick={() => setDetailModalOpen(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedLicense && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                {selectedLicense.displayName}
              </Typography>
              
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Kontaktinformationen</Typography>
                  <Typography variant="body2">E-Mail: {selectedLicense.email}</Typography>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2">Lizenzinformationen</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" sx={{ mr: 1 }}>Status:</Typography>
                    <Chip 
                      label={selectedLicense.licenseStatus} 
                      color={getLicenseStatusColor(selectedLicense.licenseStatus) as any}
                      size="small"
                    />
                  </Box>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Gültig bis: {formatDate(selectedLicense.subscribedUntil)}
                  </Typography>
                  <Typography variant="body2">
                    Verbleibende Tage: {selectedLicense.daysRemaining > 0 ? selectedLicense.daysRemaining : 'Abgelaufen'}
                  </Typography>
                </Grid>
                
                {selectedLicense.history && selectedLicense.history.length > 0 && (
                  <Grid item xs={12} sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>Lizenzhistorie</Typography>
                    <TableContainer component={Paper} variant="outlined">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Typ</TableCell>
                            <TableCell>Von</TableCell>
                            <TableCell>Bis</TableCell>
                            <TableCell>Transaktions-ID</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {selectedLicense.history.map((entry, index) => (
                            <TableRow key={index}>
                              <TableCell>{entry.type}</TableCell>
                              <TableCell>{formatDate(entry.startDate)}</TableCell>
                              <TableCell>{formatDate(entry.endDate)}</TableCell>
                              <TableCell>{entry.transactionId}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailModalOpen(false)}>Schließen</Button>
          {selectedLicense && (
            <>
              <Button 
                onClick={() => {
                  setDetailModalOpen(false);
                  handleOpenExtendLicense(selectedLicense);
                }} 
                color="secondary"
                startIcon={<CalendarMonthIcon />}
              >
                Verlängern
              </Button>
              <Button 
                onClick={() => {
                  setDetailModalOpen(false);
                  handleEditLicense(selectedLicense);
                }} 
                color="primary"
                startIcon={<EditIcon />}
              >
                Bearbeiten
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LicenseAdmin; 